import { useId } from 'react';

import get from 'lodash-es/get';
import { FieldError, useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { QuestionCondition } from '@/shared/types/action';
import { capitalizeFirstLetter } from '@/util/util';
import {
  earlierChoiceQuestions,
  QuestionSetForm,
} from '@/util/validation/schemas/question-set-schemas';

import { NO_CONDITION } from './questionSetForm';

type ConditionPath =
  `questions.${number}.show_if` | `questions.${number}.options.${number}.show_if`;

// A `FormField` on `show_if.key` would register the path and turn an absent condition into
// `{ key: undefined }`, which the schema rejects. So nothing below the object is registered,
// a nested message is read by path and the `aria-*` wiring is done here.
const ConditionMessage = ({ id, message }: { id: string; message?: string }) =>
  message ? (
    <p id={id} className="text-sm text-warning">
      {capitalizeFirstLetter(message)}
    </p>
  ) : null;

type Props = {
  /** Form path of the `show_if` this picker edits. */
  name: ConditionPath;
  /** The question this condition belongs to; it may point at an earlier choice question. */
  questionIndex: number;
  onCommit: () => void;
};

/** "Show only if": an earlier choice question, then the values that reveal this item. */
export const ShowIfFields = ({ name, questionIndex, onCommit }: Props) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<QuestionSetForm>();
  const condition = useWatch({ control, name }) as QuestionCondition | undefined;
  const questions = useWatch({ control, name: 'questions' });
  const { errors } = useFormState({ control, name });
  const id = useId();

  const earlier = earlierChoiceQuestions(questions ?? [], questionIndex);
  if (earlier.length === 0) {
    return null;
  }

  const target = earlier.find((question) => question.key === condition?.key);
  const selected = condition?.in ?? [];
  const keyError = (get(errors, `${name}.key`) as FieldError | undefined)?.message;
  const valuesError = (get(errors, `${name}.in`) as FieldError | undefined)?.message;

  const handleKeyChange = (key: string) => {
    setValue(name, key === NO_CONDITION ? undefined : { key, in: [] }, { shouldValidate: true });
    onCommit();
  };

  // The whole condition is set each time: nothing below the object is registered.
  const handleToggleValue = (value: string, checked: boolean) => {
    const next = checked ? [...selected, value] : selected.filter((v) => v !== value);
    setValue(name, { key: condition?.key ?? '', in: next }, { shouldValidate: true });
    onCommit();
  };

  return (
    <div className="flex flex-col gap-2 rounded-md bg-muted/50 p-2">
      <div className="flex flex-col gap-2">
        <Label htmlFor={`${id}-key`}>{t('dialog.question_set.show_if')}</Label>
        <Select value={condition?.key ?? NO_CONDITION} onValueChange={handleKeyChange}>
          <SelectTrigger
            id={`${id}-key`}
            aria-invalid={Boolean(keyError)}
            aria-describedby={keyError ? `${id}-key-error` : undefined}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_CONDITION}>{t('dialog.question_set.show_if_none')}</SelectItem>
            {/* Radix throws on an empty item value; a key is empty while it is retyped. */}
            {earlier
              .filter((question) => question.key)
              .map((question) => (
                <SelectItem key={question.key} value={question.key}>
                  {question.header} ({question.key})
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        <ConditionMessage id={`${id}-key-error`} message={keyError} />
      </div>
      {target && (
        <div className="flex flex-col gap-2">
          <Label id={`${id}-values`}>{t('dialog.question_set.show_if_values')}</Label>
          <div
            role="group"
            aria-labelledby={`${id}-values`}
            aria-describedby={valuesError ? `${id}-values-error` : undefined}
            className="flex flex-col gap-1"
          >
            {(target.options ?? []).map((option) => {
              const optionId = `${id}-${option.value}`;
              return (
                <div key={option.value} className="flex items-center gap-2">
                  <Checkbox
                    id={optionId}
                    checked={selected.includes(option.value)}
                    onCheckedChange={(checked) => handleToggleValue(option.value, checked === true)}
                  />
                  <Label htmlFor={optionId} className="text-sm font-normal">
                    {option.label}
                  </Label>
                </div>
              );
            })}
          </div>
          <ConditionMessage id={`${id}-values-error`} message={valuesError} />
        </div>
      )}
    </div>
  );
};
